import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
              文章未找到
            </h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          <header className="py-6 md:py-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000
                  ? `${(article.views / 10000).toFixed(1)}万 阅读`
                  : `${article.views} 阅读`}
              </span>
              <span className="flex items-center gap-1 text-accent-600 font-medium">
                <i className="ri-star-fill"></i>
                {article.rating}
              </span>
            </div>
          </header>

          <figure className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </figure>

          <div
            className="article-content max-w-none mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 pt-6 mb-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-600 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/70 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '伦理剧精选',
              logo: {
                '@type': 'ImageObject',
                url: 'https://lunliju.example.com/logo.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://lunliju.example.com/article/${article.slug}`,
            },
          }),
        }}
      />
    </>
  );
}